import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, BiasBadge, LiveDot, DemoTag, AiText, Spinner, fmt } from "../components/ui-kit";
import { PriceChart } from "../components/Charts";
import { Sparkles, HelpCircle, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const trendColor = (t) => (t === "Bullish" ? "text-bull" : t === "Bearish" ? "text-bear" : "text-slate-400");

export default function Intelligence({ instrument }) {
  const accent = instrument === "XAUUSD" ? "gold" : "btc";
  const color = accent === "gold" ? "#D4AF37" : "#F7931A";
  const [d, setD] = useState(null);
  const [report, setReport] = useState("");
  const [repLoading, setRepLoading] = useState(false);
  const [why, setWhy] = useState("");
  const [whyLoading, setWhyLoading] = useState(false);

  useEffect(() => {
    setD(null); setReport(""); setWhy("");
    client.get(`/market/${instrument}/intelligence`).then(({ data }) => setD(data));
  }, [instrument]);

  const genReport = async () => {
    setRepLoading(true); setReport("");
    try {
      const { data } = await client.get(`/market/${instrument}/report`);
      setReport(data.report);
    } catch { toast.error("Report failed"); } finally { setRepLoading(false); }
  };
  const askWhy = async () => {
    setWhyLoading(true); setWhy("");
    try {
      const { data } = await client.post("/market/why-moving", { prompt: "why", context: accent });
      setWhy(data.answer);
    } catch { toast.error("Failed"); } finally { setWhyLoading(false); }
  };

  const title = instrument === "XAUUSD" ? "XAUUSD Gold Intelligence" : "BTCUSD Bitcoin Intelligence";
  if (!d) return <Layout title={title}><Spinner /></Layout>;
  const up = d.price.change_pct >= 0;

  return (
    <Layout title={title}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          {/* Price header */}
          <Panel testid="intel-price-panel">
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`font-head text-xl font-bold ${accent === "gold" ? "text-gold" : "text-btc"}`}>{instrument}</span>
                    <BiasBadge bias={d.bias.bias} /><LiveDot />
                  </div>
                  <div className="font-mono text-4xl font-semibold text-white mt-2 tracking-tight">{fmt(d.price.price)}</div>
                </div>
                <div className="text-right font-mono text-sm">
                  <div className={`flex items-center gap-1 justify-end ${up ? "text-bull" : "text-bear"}`}>
                    {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}{up ? "+" : ""}{fmt(d.price.change_pct)}%
                  </div>
                  <div className="text-slate-500 mt-1">H {fmt(d.price.day_high)}</div>
                  <div className="text-slate-500">L {fmt(d.price.day_low)}</div>
                </div>
              </div>
              <PriceChart data={d.spark} color={color} />
            </div>
          </Panel>

          {/* MTF */}
          <Panel title="Multi-Timeframe Trend" right={<DemoTag />}>
            <div className="grid grid-cols-3 sm:grid-cols-6">
              {d.mtf.map((m) => (
                <div key={m.tf} className="p-3 border-r border-b border-[color:var(--border-subtle)] text-center">
                  <div className="font-mono text-xs text-slate-500">{m.tf}</div>
                  <div className={`text-sm font-medium mt-1 ${trendColor(m.trend)}`}>{m.trend}</div>
                  <div className="mt-2 h-1 bg-white/5"><div className="h-full" style={{ width: `${m.strength}%`, background: color }} /></div>
                </div>
              ))}
            </div>
          </Panel>

          {/* SMC structure */}
          <Panel title="Market Structure & Liquidity (SMC)">
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Row k="Structure" v={d.smc.market_structure} />
              <Row k="Break of Structure" v={d.smc.bos} />
              <Row k="Change of Character" v={d.smc.choch} />
              <Row k="Liquidity" v={d.smc.liquidity} />
              <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Fair Value Gaps</div>
                  {d.smc.fair_value_gaps.map((f, i) => <div key={i} className="font-mono text-xs text-slate-300">{f.type}: {f.zone}</div>)}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Order Blocks</div>
                  {d.smc.order_blocks.map((f, i) => <div key={i} className="font-mono text-xs text-slate-300">{f.type}: {f.zone}</div>)}
                </div>
              </div>
            </div>
          </Panel>

          {/* AI Report */}
          <Panel title="AI Research Report" right={
            <button data-testid="gen-report-btn" onClick={genReport} disabled={repLoading} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60">
              <Sparkles size={13} />{repLoading ? "Generating…" : "Generate"}
            </button>}>
            <div className="p-4" data-testid="ai-report-body">
              {repLoading ? <Spinner label="AI drafting report" /> : report ? <AiText text={report} /> :
                <p className="text-sm text-slate-500">Click Generate for a structured AI report (bias, structure, macro/derivatives, scenarios, invalidation, confidence).</p>}
            </div>
          </Panel>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <button data-testid="why-moving-btn" onClick={askWhy} disabled={whyLoading}
            className="w-full surface surface-hover p-4 flex items-center gap-3 text-left transition-colors duration-200 disabled:opacity-60">
            <HelpCircle size={20} className={accent === "gold" ? "text-gold" : "text-btc"} />
            <div>
              <div className="text-sm font-medium text-white">Why is {d.price.name} moving?</div>
              <div className="text-xs text-slate-500">One-click AI cause analysis</div>
            </div>
          </button>
          {(whyLoading || why) && (
            <Panel title="Cause Analysis">
              <div className="p-4">{whyLoading ? <Spinner /> : <AiText text={why} />}</div>
            </Panel>
          )}

          <Panel title="Indicators">
            <div className="p-4 space-y-2 text-sm">
              <Row k="RSI" v={<span className="font-mono">{d.indicators.rsi}</span>} />
              <Row k="ATR" v={<span className="font-mono">{fmt(d.indicators.atr)}</span>} />
              <Row k="EMA" v={d.indicators.ema_structure} />
              <Row k="Momentum" v={d.indicators.momentum} />
              <Row k="Volatility" v={d.indicators.volatility_state} />
            </div>
          </Panel>

          {d.macro && (
            <Panel title="Gold Macro">
              <div className="p-4 space-y-2 text-sm">
                <Row k="DXY" v={<span className="font-mono">{fmt(d.macro.dxy)}</span>} />
                <Row k="US 10Y" v={<span className="font-mono">{fmt(d.macro.us10y)}%</span>} />
                <Row k="US 2Y" v={<span className="font-mono">{fmt(d.macro.us2y)}%</span>} />
                <Row k="Rate Exp." v={d.macro.rate_expectation} />
                <Row k="Real Yields" v={d.macro.real_yields} />
                <Row k="Geopolitics" v={d.macro.geopolitics} />
              </div>
            </Panel>
          )}
          {d.derivatives && (
            <Panel title="BTC Derivatives">
              <div className="p-4 space-y-2 text-sm">
                <Row k="Open Interest" v={<span className="font-mono">{d.derivatives.open_interest} ({d.derivatives.oi_change_pct}%)</span>} />
                <Row k="Funding" v={<span className="font-mono">{d.derivatives.funding_rate}</span>} />
                <Row k="Long/Short" v={<span className="font-mono">{d.derivatives.long_short_ratio}</span>} />
                <Row k="Liquidations" v={`${d.derivatives.liquidations_24h} (${d.derivatives.liq_side})`} />
                <Row k="Dominance" v={<span className="font-mono">{d.derivatives.btc_dominance}%</span>} />
                <Row k="ETF Flow" v={<span className="font-mono">{d.derivatives.etf_flow}</span>} />
                <Row k="Exchange Netflow" v={d.derivatives.exchange_netflow} />
                <Row k="CME Gap" v={<span className="font-mono">{d.derivatives.cme_gap}</span>} />
              </div>
            </Panel>
          )}

          <Panel title="Key Levels">
            <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
              <L k="PDH" v={d.levels.prev_day_high} /><L k="PDL" v={d.levels.prev_day_low} />
              <L k="PWH" v={d.levels.prev_week_high} /><L k="PWL" v={d.levels.prev_week_low} />
              <L k="Asia H" v={d.levels.asia_high} /><L k="Asia L" v={d.levels.asia_low} />
              <L k="London H" v={d.levels.london_high} /><L k="London L" v={d.levels.london_low} />
              <L k="NY H" v={d.levels.ny_high} /><L k="NY L" v={d.levels.ny_low} />
            </div>
          </Panel>
        </div>
      </div>
    </Layout>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-slate-500 shrink-0">{k}</span>
    <span className="text-slate-200 text-right">{v}</span>
  </div>
);
const L = ({ k, v }) => (
  <div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="text-slate-300">{fmt(v)}</span></div>
);
